import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import { useCart } from '../services/CartContext';

export default function Cart({ onNavigate }) {
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const {
    cartItems,
    isCartOpen,
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    cartTotal,
    clearCart,
  } = useCart();

  const handleClose = () => setIsCartOpen(false);

  return (
    <Drawer
      anchor="right"
      open={isCartOpen}
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(15, 46, 43, 0.4)',
          },
        },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '420px' },
          backgroundColor: '#0F2E2B',
          color: '#F5F1E6',
          boxSizing: 'border-box',
          borderLeft: '1px solid rgba(245, 241, 230, 0.14)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.5)',
        },
      }}
    >
      {/* Cart Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(245, 241, 230, 0.1)',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: '"Fraunces", serif',
            fontWeight: 700,
            color: '#F5F1E6',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <ShoppingBagOutlinedIcon sx={{ color: '#D4A24C' }} />
          Your Cart
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: 'rgba(245, 241, 230, 0.66)',
            '&:hover': { color: '#F5F1E6', backgroundColor: 'rgba(245, 241, 230, 0.05)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Cart Body */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
        {cartItems.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              px: 2,
            }}
          >
            {/* Elegant Ring & Icon Graphic */}
            <Box
              sx={{
                width: 90,
                height: 90,
                borderRadius: '50%',
                background: 'conic-gradient(from 180deg, #D4A24C, #E4572E, #2F8F76, #D4A24C)',
                display: 'grid',
                placeItems: 'center',
                mb: 3,
                position: 'relative',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 3,
                  backgroundColor: '#0F2E2B',
                  borderRadius: '50%',
                  zIndex: 1,
                },
              }}
            >
              <ShoppingBagOutlinedIcon
                sx={{
                  fontSize: 40,
                  color: '#D4A24C',
                  zIndex: 2,
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                mb: 1.5,
                color: '#F5F1E6',
              }}
            >
              Your cart is empty
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(245, 241, 230, 0.66)',
                maxWidth: '280px',
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Unlock premium brand deals and cashback rewards by adding a digital coupon card to your cart.
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                handleClose();
                if (onNavigate) {
                  onNavigate('coupons');
                }
              }}
              sx={{
                backgroundColor: '#D4A24C',
                color: '#0F2E2B',
                fontWeight: 600,
                px: 4,
                py: 1.2,
                borderRadius: '999px',
                '&:hover': {
                  backgroundColor: '#E8C078',
                },
              }}
            >
              Start Shopping
            </Button>
          </Box>
        ) : (
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {cartItems.map((item) => (
              <ListItem
                key={item.id}
                disablePadding
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(21, 58, 53, 0.4)',
                  border: '1px solid rgba(245, 241, 230, 0.08)',
                  borderRadius: '16px',
                  p: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  gap: 2,
                }}
              >
                {/* Decorative Tier Gradient block */}
                <Box
                  sx={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    background: item.bg || 'linear-gradient(135deg, #D69A6C 0%, #A9673A 80%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0F2E2B',
                    fontWeight: 700,
                    fontSize: '11px',
                    fontFamily: '"Fraunces", serif',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                  }}
                >
                  {item.tier}
                </Box>

                {/* Details */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: '#F5F1E6',
                      lineHeight: 1.3,
                      fontFamily: '"Fraunces", serif',
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#D4A24C',
                      fontWeight: 700,
                      mt: 0.5,
                      fontFamily: '"JetBrains Mono", monospace',
                    }}
                  >
                    ₹{item.price}
                  </Typography>

                  {/* Quantity Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      sx={{
                        color: 'rgba(245, 241, 230, 0.66)',
                        border: '1px solid rgba(245, 241, 230, 0.15)',
                        p: 0.4,
                        '&:hover': { color: '#F5F1E6', borderColor: '#F5F1E6' },
                      }}
                    >
                      <RemoveIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                    <Typography
                      sx={{
                        minWidth: '24px',
                        textAlign: 'center',
                        fontWeight: 600,
                        fontSize: '14px',
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    >
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      disabled={item.quantity >= 3}
                      sx={{
                        color: 'rgba(245, 241, 230, 0.66)',
                        border: '1px solid rgba(245, 241, 230, 0.15)',
                        p: 0.4,
                        '&:hover': { color: '#F5F1E6', borderColor: '#F5F1E6' },
                        '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.25)', borderColor: 'rgba(245, 241, 230, 0.08)' },
                      }}
                    >
                      <AddIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* Remove button */}
                <IconButton
                  onClick={() => removeFromCart(item.id)}
                  sx={{
                    alignSelf: 'flex-start',
                    color: 'rgba(245, 241, 230, 0.4)',
                    '&:hover': { color: '#E4572E', backgroundColor: 'rgba(228, 87, 46, 0.08)' },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Cart Footer */}
      {cartItems.length > 0 && (
        <Box
          sx={{
            p: 3,
            borderTop: '1px solid rgba(245, 241, 230, 0.1)',
            backgroundColor: 'rgba(21, 58, 53, 0.6)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body1" sx={{ color: 'rgba(245, 241, 230, 0.7)' }}>
              Subtotal
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#D4A24C',
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              ₹{cartTotal}
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));
              if (isLoggedIn) {
                setIsCartOpen(false);
                if (onNavigate) onNavigate('checkout');
              } else {
                setLoginDialogOpen(true);
              }
            }}
            sx={{
              backgroundColor: '#D4A24C',
              color: '#0F2E2B',
              fontWeight: 700,
              fontSize: '15px',
              py: 1.4,
              borderRadius: '999px',
              '&:hover': {
                backgroundColor: '#E8C078',
              },
              boxShadow: '0 8px 24px rgba(212, 162, 76, 0.25)',
              mb: 1.5,
            }}
          >
            Proceed to Checkout
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={clearCart}
            sx={{
              color: 'rgba(245, 241, 230, 0.5)',
              fontWeight: 500,
              fontSize: '13px',
              '&:hover': {
                color: '#E4572E',
                backgroundColor: 'transparent',
              },
            }}
          >
            Clear Cart
          </Button>

          {/* Login Required Dialog */}
          <Dialog
            open={loginDialogOpen}
            onClose={() => setLoginDialogOpen(false)}
            disableScrollLock={true}
            slotProps={{
              paper: {
                sx: {
                  background: '#153A35',
                  border: '1px solid rgba(245, 241, 230, 0.14)',
                  color: '#F5F1E6',
                  borderRadius: '20px',
                  p: 3,
                  maxWidth: '400px',
                },
              },
            }}
          >
            <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, p: 0, mb: 1.5 }}>
              Login Required
            </DialogTitle>
            <DialogContent sx={{ p: 0, mb: 3 }}>
              <Typography sx={{ color: 'rgba(245, 241, 230, 0.7)', fontSize: '14.5px', lineHeight: 1.5 }}>
                Please log in to your account to complete your checkout and activate your coupon cards.
              </Typography>
            </DialogContent>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setLoginDialogOpen(false)}
                sx={{
                  borderColor: 'rgba(245, 241, 230, 0.14)',
                  color: '#F5F1E6',
                  borderRadius: '999px',
                  px: 3,
                  '&:hover': { borderColor: '#F5F1E6', backgroundColor: 'rgba(245,241,230,0.05)' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setLoginDialogOpen(false);
                  setIsCartOpen(false);
                  if (onNavigate) onNavigate('login');
                }}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 600,
                  borderRadius: '999px',
                  px: 3,
                  '&:hover': { backgroundColor: '#E8C078' },
                }}
              >
                Login Now
              </Button>
            </Box>
          </Dialog>
        </Box>
      )}
    </Drawer>
  );
}
